import { useMemo, useState } from 'react';
import {
    normaliseSelection,
    selectionTotals,
    toggleSelection,
    unitKey,
} from '../utils/invoiceSelection';
import type { SelectionTotals, SelectionUnit } from '../utils/invoiceSelection';
import { selectionSummaryLines } from '../utils/selectionSummaryLines';
import type { SelectionSummaryLine } from '../utils/selectionSummaryLines';
import type { SoldLines } from '../types/agreements.types';

const EMPTY_LINES: SoldLines = { services: [], addOns: [] };

interface SelectionState {
    agreementId: string | undefined;
    units: SelectionUnit[];
}

export interface InvoiceSelection {
    selection: SelectionUnit[];
    toggle: (unit: SelectionUnit) => void;
    clear: () => void;
    clearUnits: (units: SelectionUnit[]) => void;
    totals: SelectionTotals;
    summaryLines: SelectionSummaryLine[];
}

/** Local state only — not persisted, not in Redux (6.5). Resets on an agreement id change. */
export function useInvoiceSelection(
    agreementId: string | undefined,
    lines: SoldLines | undefined,
    flexible: boolean,
): InvoiceSelection {
    const [state, setState] = useState<SelectionState>({ agreementId, units: [] });

    // Tracked-previous-value pattern: re-key the selection to empty during
    // render when the agreement id changes, with no effect required.
    let current = state;
    if (state.agreementId !== agreementId) {
        current = { agreementId, units: [] };
        setState(current);
    }

    const selection = useMemo(
        () => normaliseSelection(current.units, lines, flexible),
        [current.units, lines, flexible],
    );

    const toggle = (unit: SelectionUnit): void => {
        if (!lines) return;
        setState({ agreementId, units: toggleSelection(selection, unit, lines, flexible) });
    };

    const clear = (): void => setState({ agreementId, units: [] });

    const clearUnits = (units: SelectionUnit[]): void => {
        const removed = new Set(units.map(unitKey));
        setState((previous) =>
            previous.agreementId !== agreementId
                ? previous
                : { agreementId, units: previous.units.filter((unit) => !removed.has(unitKey(unit))) },
        );
    };

    const totals = useMemo(
        () => selectionTotals(selection, lines ?? EMPTY_LINES),
        [selection, lines],
    );

    const summaryLines = useMemo(
        () => selectionSummaryLines(selection, lines ?? EMPTY_LINES),
        [selection, lines],
    );

    return { selection, toggle, clear, clearUnits, totals, summaryLines };
}
